import type { Tables } from '@auctisync/shared';

import type { AuctisyncSupabase } from './client.js';

/**
 * Which sales are OPEN right now — the reader behind "which sale am I in".
 *
 * The website's Browse page had this query inline and the app had none, so the
 * phone's Browse header could not name the sale a bidder was looking at while
 * the website's toolbar could. The design puts the sale's name in the chrome of
 * every Browse screen ("the sale line doubles as the way into the sale layer"),
 * which means both surfaces must answer "which sale" the SAME way. This module
 * is that one answer.
 *
 * "Open" is `active` or `preview`: a sale bidders can look at. A completed sale
 * is not open, however recently it closed — it has a results page and no live
 * lots, and naming it at the top of Browse would promise bidding that cannot
 * happen.
 */
export type OpenSale = Pick<
  Tables<'auctions'>,
  'id' | 'slug' | 'title' | 'tenant_id' | 'status' | 'ends_at' | 'buyers_premium_bps'
>;

export const OPEN_SALE_STATUSES = ['active', 'preview'] as const;

/**
 * Every visible open sale, soonest-closing first. Scoped to one house when a
 * tenant id is given, across every published house when it is null — the same
 * two shapes the lot feed takes.
 *
 * Never throws: a header that cannot name the sale shows no sale line, which
 * is better than a header that shows an error where a name should be.
 */
export async function fetchOpenSales(
  supabase: AuctisyncSupabase,
  tenantId: string | null,
): Promise<OpenSale[]> {
  let query = supabase
    .from('auctions')
    /* `buyers_premium_bps` joined the projection on 2026-09-22 for the phone's
       exposure meter, which needs the rate to show an all-in total and has no
       other read of the sale. One more column on a query already being made. */
    .select('id, slug, title, tenant_id, status, ends_at, buyers_premium_bps')
    .eq('is_visible', true)
    .in('status', [...OPEN_SALE_STATUSES])
    .order('ends_at', { ascending: true, nullsFirst: false });
  if (tenantId !== null) query = query.eq('tenant_id', tenantId);

  const { data, error } = await query;
  if (error) return [];
  return (data ?? []) as OpenSale[];
}

/**
 * The sale a bidder means by "browse".
 *
 * A URL (or deep link) that names a sale wins — but only if that sale is
 * actually open, so a stale link cannot select a sale nobody can enter.
 * Otherwise the one closing soonest, because that is the one with something
 * happening in it. Pure, so the rule can be tested at its boundaries.
 */
export function chooseOpenSale<T extends Pick<OpenSale, 'slug'>>(
  sales: readonly T[],
  preferredSlug?: string | null,
): T | null {
  if (preferredSlug) {
    const named = sales.find((s) => s.slug === preferredSlug);
    if (named) return named;
  }
  return sales[0] ?? null;
}

/**
 * What the Browse header says beneath the house's name.
 *
 * One open sale → its title. Several → the soonest-closing one's title, since
 * that is the sale the lots on screen are mostly from, and the count so the
 * bidder knows there is more behind the tap. None → null, and the header shows
 * no sale line at all rather than inventing one.
 */
export function saleLine(sales: readonly Pick<OpenSale, 'slug' | 'title'>[]): string | null {
  const chosen = chooseOpenSale(sales);
  if (chosen === null) return null;
  const title = chosen.title.trim();
  if (sales.length === 1) return title;
  return `${title} · ${String(sales.length - 1)} more`;
}

/**
 * Everything the SALE HEADER on a catalogue screen needs, in one read.
 *
 * 🔴 WHY A SECOND READER AND NOT A WIDER `fetchOpenSales`. Jim, 2026-09-11,
 * pointing at an incumbent's auction page: *"You see how auction information
 * stays at the top of the page? Adding this to the website on the Browse and
 * Live Now pages would look good."* He is right, and it is a hole in our own
 * product rather than a competitor's feature: an OPEN sale has no landing page
 * (`/auctions/:slug` redirects to Browse), so Browse *is* the sale page — and
 * it carried one line of grey text while the Sales LIST page has had a full
 * summary card all along.
 *
 * `fetchOpenSales` stays narrow because the PHONE calls it, on every Browse
 * mount, only ever to name the sale in the header. Widening it would make every
 * app launch pay for twenty columns it does not draw. This is the website's
 * read, for the one sale a bidder is actually looking at.
 *
 * The field list is deliberately a superset of `SaleTermsModal`'s: the header's
 * "Sale details" button opens that modal, and the modal already covers all four
 * things the incumbent splits across separate buttons — description, terms,
 * removal times and preview dates. One read feeds both.
 */
export type SaleHeader = Pick<
  Tables<'auctions'>,
  // Identity and state
  | 'id'
  | 'slug'
  | 'title'
  | 'status'
  | 'lot_count'
  // Timing. The `hide_*` flags are real and enforced in SQL — a house that has
  // chosen not to publish its close time must not have it printed here.
  | 'starts_at'
  | 'ends_at'
  | 'hide_starts_at'
  | 'hide_ends_at'
  // What the modal reads
  | 'description'
  | 'buyers_premium_bps'
  | 'online_premium_bps'
  | 'requires_terms_acceptance'
  | 'requires_bidder_approval'
  | 'requires_cc_preauth'
  | 'cc_preauth_amount_cents'
  | 'extension_threshold_seconds'
  | 'extension_amount_seconds'
  | 'preview_text'
  | 'preview_starts_at'
  | 'preview_ends_at'
  | 'pickup_location'
  | 'pickup_address_line1'
  | 'pickup_address_line2'
  | 'pickup_city'
  | 'pickup_region'
  | 'pickup_postal_code'
  | 'pickup_instructions'
  /*
   * Collection windows. Selected since 2026-09-18, when the auction form grew
   * the scheduler that writes them — until then every row held the column's
   * `[]` default, so fetching it would have cost a column for a guaranteed
   * empty array. Callers pass it through `parsePickupWindows`; `resolvePickup`
   * then prefers a dated window over the house's prose.
   */
  | 'pickup_dates'
  /*
   * The zone this sale's times are stated in. Selected since 2026-09-18: a
   * close time shown in the reader's zone is a different fact from the one the
   * house published, and a bidder deciding whether they can make a collection
   * needs the warehouse's clock, not their own.
   */
  | 'timezone'
>;

const SALE_HEADER_COLUMNS =
  'id, slug, title, status, lot_count, starts_at, ends_at, hide_starts_at, hide_ends_at, ' +
  'description, buyers_premium_bps, online_premium_bps, requires_terms_acceptance, ' +
  'requires_bidder_approval, requires_cc_preauth, cc_preauth_amount_cents, ' +
  'extension_threshold_seconds, extension_amount_seconds, preview_text, preview_starts_at, ' +
  'preview_ends_at, pickup_location, pickup_address_line1, pickup_address_line2, pickup_city, ' +
  'pickup_region, pickup_postal_code, pickup_instructions, pickup_dates, timezone';

/**
 * One open sale, by slug, for the header above a catalogue.
 *
 * Never throws. A header that cannot describe the sale renders nothing, and the
 * lots below it are unaffected — the catalogue is the page's job, and it must
 * not fail because a premium percentage could not be read.
 *
 * `is_visible` and the open statuses are re-asserted here rather than trusted
 * from the caller: this returns publicly readable sale terms, and a slug in a
 * URL is not an authorisation.
 */
export async function fetchSaleHeader(
  supabase: AuctisyncSupabase,
  slug: string,
): Promise<SaleHeader | null> {
  const { data, error } = await supabase
    .from('auctions')
    .select(SALE_HEADER_COLUMNS)
    .eq('slug', slug)
    .eq('is_visible', true)
    .in('status', [...OPEN_SALE_STATUSES])
    .maybeSingle();
  if (error) return null;
  return (data as SaleHeader | null) ?? null;
}

/**
 * The close time a bidder is allowed to see, or null.
 *
 * Pulled out of the component because the app will want the same answer the day
 * it grows a sale header of its own, and because "is this publishable" is a
 * RULE — the kind that must live once. `hide_ends_at` is enforced in SQL as
 * well; this stops the website printing a time the database would refuse to
 * confirm.
 */
export function publishableCloseAt(
  sale: Pick<SaleHeader, 'ends_at' | 'hide_ends_at'>,
): string | null {
  if (sale.hide_ends_at) return null;
  return sale.ends_at;
}

/**
 * The sale's close time, falling back to the LAST lot still to close.
 *
 * 🔴 WHY A FALLBACK EXISTS AT ALL. `auctions.ends_at` is optional and is simply
 * not set on plenty of real sales — a staggered timed auction closes when its
 * last lot closes, and the house never types a sale-level time. The header then
 * printed no close at all, which is the single fact a bidder most needs, while
 * every lot on the page underneath it was counting down. The band looked empty
 * because it was refusing to state something the page already knew.
 *
 * 🔴 `hide_ends_at` STILL WINS, AND THAT IS THE WHOLE POINT OF ROUTING THIS
 * THROUGH ONE FUNCTION. A house that has chosen not to publish its close time
 * must not have one computed and printed for it — deriving around a privacy
 * flag is worse than ignoring it, because it looks authoritative. The flag is
 * enforced in SQL too; this keeps the website from trying.
 *
 * The lots are the ones the caller is DISPLAYING, so this cannot drift from
 * what the bidder sees — the same reasoning as summing money from displayed
 * rows rather than trusting a stored aggregate.
 */
export function saleCloseAt(
  sale: Pick<SaleHeader, 'ends_at' | 'hide_ends_at'>,
  lotEndsAt: readonly (string | null)[] = [],
): string | null {
  if (sale.hide_ends_at) return null;
  if (sale.ends_at !== null) return sale.ends_at;
  const latest = lotEndsAt
    .filter((t): t is string => typeof t === 'string' && t !== '')
    .sort()
    .at(-1);
  return latest ?? null;
}

/**
 * When the sale STARTS closing — the first lot to close, not the last.
 *
 * 🔴 THIS IS A DIFFERENT FACT FROM `saleCloseAt`, AND IT IS THE ONE A BIDDER
 * PLANS AROUND. Jim, 2026-09-18, asked for a calendar entry "for the time lots
 * begin to close". In a staggered sale those are hours apart: `saleCloseAt`
 * answers "when is it over" and this answers "when do I need to be watching".
 * An entry on the last lot's time would have a bidder show up after most of the
 * sale had already gone.
 *
 * 🔴 `hide_ends_at` STILL WINS. Same reasoning as `saleCloseAt`: deriving
 * around a privacy flag is worse than ignoring it, because the computed answer
 * looks authoritative. A house that hid its close time gets no calendar entry
 * either — the button simply does not render.
 *
 * Prefers the lots because they are the precise answer and the sale-level
 * `ends_at` is the coarse one — the reverse of `saleCloseAt`, deliberately. A
 * house that typed a sale `ends_at` AND staggers its lots is describing the
 * end; the lots know when the first gavel falls. Falls back to `ends_at` when
 * the caller has no lot times, so a header that has not loaded lots still
 * offers something rather than nothing.
 */
export function saleClosingBeginsAt(
  sale: Pick<SaleHeader, 'ends_at' | 'hide_ends_at'>,
  lotEndsAt: readonly (string | null)[] = [],
): string | null {
  if (sale.hide_ends_at) return null;
  const earliest = lotEndsAt
    .filter((t): t is string => typeof t === 'string' && t !== '')
    .sort()
    .at(0);
  return earliest ?? sale.ends_at;
}

/** A buyer's premium as a display percentage, or null when the house set none. */
export function premiumPercent(bps: number | null | undefined): string | null {
  if (bps === null || bps === undefined || bps <= 0) return null;
  const pct = bps / 100;
  // Trim a trailing `.0` so 15% reads as "15%" and 12.5% keeps its half.
  return `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(2).replace(/0$/, '')}%`;
}

/**
 * The preview window as a pair, or null when the house has published neither
 * end of it.
 *
 * A start with no end is still worth showing ("Preview from…"), which is why
 * this returns the pair rather than requiring both.
 */
export function previewWindow(
  sale: Pick<SaleHeader, 'preview_starts_at' | 'preview_ends_at'>,
): { startsAt: string | null; endsAt: string | null } | null {
  if (sale.preview_starts_at === null && sale.preview_ends_at === null) return null;
  return { startsAt: sale.preview_starts_at, endsAt: sale.preview_ends_at };
}

/**
 * Where a winner collects, as one line. Null when the house has published no
 * location at all.
 *
 * The city/region pair is joined with a comma and the rest with a middot, which
 * is the same shape the Sales list row uses — two surfaces describing the same
 * place the same way.
 */
export function pickupLine(
  sale: Pick<SaleHeader, 'pickup_location' | 'pickup_city' | 'pickup_region'>,
): string | null {
  const where = [sale.pickup_city, sale.pickup_region].filter(Boolean).join(', ');
  const parts = [sale.pickup_location, where].filter((p) => p !== null && p !== '');
  return parts.length === 0 ? null : parts.join(' · ');
}
